import React from "react";

import { serializeError } from "serialize-error";

import amazonLogo from "@assets/amazon-logo.svg";
import { GenericExternalLink } from "@components/generic-external-link";
import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { ConnectionType } from "@north-beam/nb-client-side";
import {
  ConnectionCard,
  KnowledgeBaseLearnMoreLink,
} from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentWithFrontendIntegrationProps,
} from "@pages/settings/connection-status-manager";
import { type CardProps2 } from "@pages/settings/core";
import { PrimaryButton } from "@shared/buttons";
import { extractError } from "@utils/index";
import { launchOAuthPopup } from "@utils/oauth2-handler";

import { type AccountMetadataWithDescription } from "../account-selector";

const CONNECTION_TYPE_NAME = "Amazon Ads";
export function AmazonConnectionCard(props: CardProps2) {
  return (
    <ConnectionCard
      type={type}
      connectionTypeName={CONNECTION_TYPE_NAME}
      connectionCallToAction={
        "Integrate your Amazon Ads data to start receiving " +
        "insights about your Amazon ad spend."
      }
      logoUrl={amazonLogo}
      logoAltText="Amazon Ads Logo"
      supportMultipleAccounts="subAccounts"
      precontent={
        <div className="mb-4 italic">
          <LearnMoreTooltip
            overlayClassName="max-w-[80%]"
            contentClassName="w-fit"
            title={`${CONNECTION_TYPE_NAME} Integration Limitations`}
            content={
              <>
                <li>
                  This connection populates <b>Spend</b> from Amazon Ads.
                </li>
                <li>
                  Spend data takes about 2-3 days to <b>fully populate</b>,
                  including data from the past{" "}
                  <b>58 days starting at the time of connection.</b>
                </li>
                <li>Amazon Vendor Central or DSP are currently unsupported.</li>
                <li>
                  You could use our{" "}
                  <GenericExternalLink href="https://docs.northbeam.io/docs/add-spend">
                    Spend Spendsheets or the Spend API
                  </GenericExternalLink>{" "}
                  to ingest Vendor Central or DSP spend data into your
                  dashboard, or ingest Ads spend prior to the 58-day limit.
                </li>
                <KnowledgeBaseLearnMoreLink
                  className="mt-4"
                  connectionTypeName={CONNECTION_TYPE_NAME}
                  knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-amazon"
                />
              </>
            }
          >
            Learn about our {CONNECTION_TYPE_NAME}{" "}
            <span className="underline text-danger">
              details and limitations
            </span>
          </LearnMoreTooltip>
        </div>
      }
    >
      {({ setStatus, accounts }: LoginComponentChildrenProps) => (
        <>
          <AmazonLoginComponent
            setStatus={setStatus}
            accounts={accounts}
            integrationDetails={props.integrationDetails}
          />
        </>
      )}
    </ConnectionCard>
  );
}

function AmazonLoginComponent({
  setStatus,
  accounts,
  integrationDetails,
}: LoginComponentWithFrontendIntegrationProps) {
  const { user } = useUser();
  const [isButtonDisabled, setButtonDisabled] = React.useState(false);

  const { amazonAuthBlob } = integrationDetails;

  const onConnectClicked = React.useCallback(async () => {
    try {
      setButtonDisabled(true);

      const tokenPayload = await openAmazonPopup(
        amazonAuthBlob.client_id,
        amazonAuthBlob.scope,
      );

      const result = await user.callApi<{
        error: string;
        accounts: AccountMetadataWithDescription[];
      }>("/api/callbacks/amazon", {
        method: "post",
        data: {
          tokenPayload,
          redirectURI: `${window.location.origin}/callbacks/amazon`,
        },
      });

      if (result.error) {
        setStatus({
          type: ConnectionStatusType.HAS_ERROR,
          errorMessage: `An error occurred: ${serializeError(
            extractError(result.error),
          )}`,
        });
      } else {
        setStatus({
          type: ConnectionStatusType.SELECTING_ACCOUNT,
          accountList: result.accounts,
        });
      }
    } catch (e) {
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage: `An error occurred: ${serializeError(extractError(e))}`,
      });
    }

    setButtonDisabled(false);
  }, [setStatus, setButtonDisabled, user, amazonAuthBlob]);

  return (
    <div className="mb-3">
      <div className="row">
        <div className="col-auto">
          <PrimaryButton disabled={isButtonDisabled} onClick={onConnectClicked}>
            {isButtonDisabled ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

async function openAmazonPopup(oauthClientId: string, oauthScopes: string) {
  const params = new URLSearchParams();

  params.append("client_id", oauthClientId);
  params.append("redirect_uri", `${window.location.origin}/callbacks/amazon`);
  params.append("response_type", "code");
  params.append("refreshable", "true");
  params.append("scope", oauthScopes);

  return launchOAuthPopup(`https://www.amazon.com/ap/oa?${params.toString()}`);
}

const type = ConnectionType.integration_amazon;

/*
  const params = [
    `consumer_id=${oauthClientId}`,
    `redirect_uri=${window.location.origin}/callbacks/amazon`,
    "response_type=code",
    "refreshable=true",
    `scope=${oauthScopes}`,
  ].join("&");
  */
